import { useSyncExternalStore } from 'react';
import { defaultApiBase } from '../config.ts';
import { fetchServerConfig, type ServerConfig } from './authClient.ts';

/**
 * Stämmer sidans webbadress med den adress servern hanterar konton på? Gäller bara webben när API:t har
 * samma ursprung som sidan. Till exempel en designförhandsvisning på http://127.0.0.1:5174 som proxar
 * /api till en server konfigurerad för http://localhost:8787: där stoppar servern inloggningen (origin-
 * kontroll), så appen ska säga det INNAN någon fyller i ett formulär. Ingen kontroll försvagas här.
 */
export type OriginCheck =
  | { status: 'not-applicable' }
  | { status: 'checking' }
  | { status: 'ok'; config: ServerConfig }
  | { status: 'mismatch'; config: ServerConfig; pageOrigin: string }
  | { status: 'unreachable' };

let current: OriginCheck = { status: 'checking' };
let started = false;
const listeners = new Set<() => void>();

export function classifyOrigin(config: ServerConfig, pageOrigin: string): OriginCheck {
  return config.publicOrigin === pageOrigin ? { status: 'ok', config } : { status: 'mismatch', config, pageOrigin };
}

function start() {
  if (started) return;
  started = true;
  if (defaultApiBase() !== '') {
    current = { status: 'not-applicable' };
    return;
  }
  fetchServerConfig('').then(
    (c) => set(classifyOrigin(c, location.origin)),
    () => set({ status: 'unreachable' }),
  );
}

function set(v: OriginCheck) {
  current = v;
  for (const l of listeners) l();
}

/** `enabled` = false gör inget anrop (t.ex. inloggad: då är adressen redan bevisat rätt). */
export function useOriginCheck(enabled = true): OriginCheck {
  if (enabled) start();
  return useSyncExternalStore(
    (cb) => {
      listeners.add(cb);
      return () => listeners.delete(cb);
    },
    () => current,
    () => current,
  );
}
